import { useThemeStore } from "@stores/theme.store";
import { Button, Select } from "antd";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { linkPage } from "@utils/router";
import { useQuery } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";
import { LuChevronRight, LuClipboardList, LuInbox } from "react-icons/lu";
import { Spiner } from "@components/loaders/spiner";
import { Permit } from "@services/dtos/permit.dto";
import { FilterOutlined } from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";
import { PermitService } from "@services/permit.service";

moment.locale("id");

const statusLabel = (acc: string) => {
  if (acc === "Y")
    return {
      label: "Diizinkan",
      bg: "#f0fdf4",
      text: "#16a34a",
      ring: "#bbf7d0",
    };
  if (acc === "N")
    return {
      label: "Ditolak",
      bg: "#fef2f2",
      text: "#dc2626",
      ring: "#fecaca",
    };
  return { label: "Menunggu", bg: "#fffbeb", text: "#d97706", ring: "#fde68a" };
};

export const PermitDataPage = () => {
  const navigate = useNavigate();
  const { setHeader, setAppBar } = useThemeStore((state) => state);
  const [limit, setLimit] = useState<string>("10");
  const [popupFilter, setPopupFilter] = useState<boolean>(false);

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data, isPending } = useQuery({
    queryKey: [PermitService.queries.GET_PERMIT_DATA, nis, limit],
    queryFn: () => PermitService.data({ nis, limit }),
  });

  const onOpenDetail = (item: Permit) => {
    navigate(linkPage.PERMIT_DETAIL_PAGE, { state: item });
  };

  useEffect(() => {
    setAppBar({ title: "Data Perizinan", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      filter: false,
      title: "Data Perizinan",
      image: false,
    });
  }, [setHeader, setAppBar]);

  return (
    <>
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        {/* filter bar */}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div className="tw-text-sm tw-text-text-secondary">
            Menampilkan{" "}
            <span className="tw-font-semibold tw-text-text-primary">
              {limit} data terakhir
            </span>
          </div>
          <FilterOutlined
            className="tw-p-2 tw-text-xl tw-bg-transparent card-body"
            onClick={() => setPopupFilter(true)}
          />
        </div>

        {/* list */}
        {isPending ? (
          <div className="tw-text-center card-body">
            <Spiner />
          </div>
        ) : data?.data != null && data.data.length > 0 ? (
          <div className="tw-space-y-3">
            {data.data.map((item, idx) => {
              const status = statusLabel(item.acc);
              return (
                <div
                  key={idx}
                  className="card-body tw-flex tw-items-center tw-gap-3 tw-cursor-pointer"
                  onClick={() => onOpenDetail(item)}
                >
                  <div
                    className="tw-flex tw-items-center tw-justify-center tw-rounded-2xl tw-w-10 tw-h-10 tw-shrink-0"
                    style={{ background: "#eef2ff" }}
                  >
                    <LuClipboardList className="tw-text-lg tw-text-[#4f46e5]" />
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-text-sm tw-font-semibold tw-text-text-primary tw-truncate">
                      Permintaan Izin
                    </div>
                    <div className="tw-text-xs tw-text-text-secondary tw-mt-0.5">
                      {moment(item.datePermit ?? Date.now()).format(
                        "DD MMM YYYY",
                      )}
                    </div>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                    <span
                      className="tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full"
                      style={{
                        background: status.bg,
                        color: status.text,
                        border: `1px solid ${status.ring}`,
                      }}
                    >
                      {status.label}
                    </span>
                    <LuChevronRight className="tw-text-base tw-text-text-secondary" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="tw-p-8 tw-text-center card-body">
            <LuInbox className="tw-mx-auto tw-mb-2 tw-text-5xl tw-text-text-secondary tw-opacity-40" />
            <div className="tw-text-sm tw-text-text-secondary">
              Data tidak ditemukan
            </div>
          </div>
        )}
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <Select
          className="tw-text-base"
          placeholder="Pilih Jumlah Ditampilkan"
          defaultValue={limit}
          style={{ width: "100%" }}
          allowClear
          onChange={(e) => setLimit(e)}
          options={[
            { value: "10", label: "10 data terakhir" },
            { value: "25", label: "25 data terakhir" },
            { value: "50", label: "50 data terakhir" },
            { value: "100", label: "100 data terakhir" },
          ]}
        />
        <Button
          onClick={() => setPopupFilter(false)}
          className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl tw-bg-red-500 tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
        >
          Tutup
        </Button>
      </PopupCustom>
    </>
  );
};
